import type { Language } from "./types.js";

type UiStrings = {
  site: {
    name: string;
    title: string;
    testTitle: string;
    surveyTitle: string;
  };
  nav: {
    exitTitle: string;
    exitDesc: string;
    exitConfirm: string;
    exitCancel: string;
    bgmLabel: string;
  };
  personalInfo: {
    select: string;
    notSay: string;
    male: string;
    female: string;
    other: string;
    nationalityPlaceholder: string;
  };
  common: {
    loading: string;
    error: string;
    retry: string;
    close: string;
    save: string;
    cancel: string;
    confirm: string;
    delete: string;
    edit: string;
    create: string;
    back: string;
    next: string;
    prev: string;
    submit: string;
    comingSoon: string;
    comingSoonDesc: string;
    link: string;
    preparing: string;
  };
  test: {
    errorLoad: string;
    notFound: string;
    resultNotFound: string;
    backHome: string;
    shareResult: string;
    shareKakao: string;
  };
  survey: {
    start: string;
    progress: string;
    result: string;
    share: string;
    errorLoad: string;
    notFound: string;
    doneTitle: string;
    doneDesc: string;
    backHome: string;
    personalInfo: {
      title: string;
      name: string;
      age: string;
      gender: string;
      nationality: string;
      agreeTerms: string;
      agreePrivacy: string;
    };
  };
  page: {
    errorLoad: string;
  };
  accessibility: {
    selectLanguage: string;
  };
  card: {
    selectOption: string;
  };
};

export const uiStrings: Record<Language, UiStrings> = {
  ko: {
    site: {
      name: "Templestay",
      title: "Templestay",
      testTitle: "Templestay — 검사",
      surveyTitle: "Templestay — 설문",
    },
    nav: {
      exitTitle: "나가시겠습니까?",
      exitDesc: "진행 중인 내용이 저장되지 않습니다.",
      exitConfirm: "나가기",
      exitCancel: "계속하기",
      bgmLabel: "배경음악",
    },
    personalInfo: {
      select: "선택 안 함",
      notSay: "선택 안 함",
      male: "남성",
      female: "여성",
      other: "기타",
      nationalityPlaceholder: "국적을 선택하세요",
    },
    common: {
      loading: "불러오는 중...",
      error: "오류가 발생했습니다",
      retry: "다시 시도",
      close: "닫기",
      save: "저장",
      cancel: "취소",
      confirm: "확인",
      delete: "삭제",
      edit: "편집",
      create: "생성",
      back: "이전",
      next: "다음",
      prev: "이전",
      submit: "제출",
      comingSoon: "구현 예정",
      comingSoonDesc: "본 개발에서 제공될 기능입니다.",
      link: "링크",
      preparing: "준비중",
    },
    test: {
      errorLoad: "검사를 불러올 수 없습니다.",
      notFound: "검사를 찾을 수 없습니다.",
      resultNotFound: "결과를 찾을 수 없습니다.",
      backHome: "처음으로",
      shareResult: "결과 공유하기",
      shareKakao: "카카오톡으로 공유",
    },
    survey: {
      start: "시작하기",
      progress: "{current}/{total}",
      result: "결과 보기",
      share: "공유하기",
      errorLoad: "설문을 불러올 수 없습니다.",
      notFound: "설문을 찾을 수 없습니다.",
      doneTitle: "응답해 주셔서 감사합니다",
      doneDesc: "소중한 의견이 서비스 개선에 활용됩니다.",
      backHome: "처음으로 돌아가기",
      personalInfo: {
        title: "개인정보 입력",
        name: "이름",
        age: "나이",
        gender: "성별",
        nationality: "국적",
        agreeTerms: "이용약관에 동의합니다",
        agreePrivacy: "개인정보 수집에 동의합니다",
      },
    },
    page: {
      errorLoad: "페이지를 불러올 수 없습니다.",
    },
    accessibility: {
      selectLanguage: "언어 선택",
    },
    card: {
      selectOption: "선택하세요",
    },
  },
  en: {
    site: {
      name: "Templestay",
      title: "Templestay",
      testTitle: "Templestay — Test",
      surveyTitle: "Templestay — Survey",
    },
    nav: {
      exitTitle: "Leave?",
      exitDesc: "Your progress will not be saved.",
      exitConfirm: "Leave",
      exitCancel: "Continue",
      bgmLabel: "BGM",
    },
    personalInfo: {
      select: "Prefer not to say",
      notSay: "Prefer not to say",
      male: "Male",
      female: "Female",
      other: "Other",
      nationalityPlaceholder: "Select nationality",
    },
    common: {
      loading: "Loading...",
      error: "An error occurred",
      retry: "Try again",
      close: "Close",
      save: "Save",
      cancel: "Cancel",
      confirm: "Confirm",
      delete: "Delete",
      edit: "Edit",
      create: "Create",
      back: "Back",
      next: "Next",
      prev: "Previous",
      submit: "Submit",
      comingSoon: "Coming Soon",
      comingSoonDesc: "This feature will be available in the full release.",
      link: "Link",
      preparing: "Preparing",
    },
    test: {
      errorLoad: "Unable to load the test.",
      notFound: "Test not found.",
      resultNotFound: "Result not found.",
      backHome: "Back to Home",
      shareResult: "Share Result",
      shareKakao: "Share via KakaoTalk",
    },
    survey: {
      start: "Start",
      progress: "{current}/{total}",
      result: "View Result",
      share: "Share",
      errorLoad: "Unable to load the survey.",
      notFound: "Survey not found.",
      doneTitle: "Thank you for your response",
      doneDesc: "Your valuable feedback will be used to improve our temple guidance.",
      backHome: "Back to Home",
      personalInfo: {
        title: "Personal Information",
        name: "Name",
        age: "Age",
        gender: "Gender",
        nationality: "Nationality",
        agreeTerms: "I agree to the terms of service",
        agreePrivacy: "I agree to the privacy policy",
      },
    },
    page: {
      errorLoad: "Unable to load the page.",
    },
    accessibility: {
      selectLanguage: "Select language",
    },
    card: {
      selectOption: "Please select",
    },
  },
  zh: {
    site: {
      name: "Templestay",
      title: "Templestay",
      testTitle: "Templestay — 测试",
      surveyTitle: "Templestay — 问卷",
    },
    nav: {
      exitTitle: "确定离开？",
      exitDesc: "当前进度将不会保存。",
      exitConfirm: "离开",
      exitCancel: "继续",
      bgmLabel: "背景音乐",
    },
    personalInfo: {
      select: "不填写",
      notSay: "不填写",
      male: "男",
      female: "女",
      other: "其他",
      nationalityPlaceholder: "请选择国籍",
    },
    common: {
      loading: "加载中...",
      error: "发生错误",
      retry: "重试",
      close: "关闭",
      save: "保存",
      cancel: "取消",
      confirm: "确认",
      delete: "删除",
      edit: "编辑",
      create: "创建",
      back: "返回",
      next: "下一步",
      prev: "上一步",
      submit: "提交",
      comingSoon: "即将推出",
      comingSoonDesc: "此功能将在正式版本中提供。",
      link: "链接",
      preparing: "准备中",
    },
    test: {
      errorLoad: "无法加载测试。",
      notFound: "未找到测试。",
      resultNotFound: "未找到结果。",
      backHome: "返回首页",
      shareResult: "分享结果",
      shareKakao: "通过KakaoTalk分享",
    },
    survey: {
      start: "开始",
      progress: "{current}/{total}",
      result: "查看结果",
      share: "分享",
      errorLoad: "无法加载问卷。",
      notFound: "未找到问卷。",
      doneTitle: "感谢您的回答",
      doneDesc: "您宝贵的意见将用于改善奉恩寺的导览服务。",
      backHome: "返回首页",
      personalInfo: {
        title: "个人信息",
        name: "姓名",
        age: "年龄",
        gender: "性别",
        nationality: "国籍",
        agreeTerms: "同意服务条款",
        agreePrivacy: "同意隐私政策",
      },
    },
    page: {
      errorLoad: "无法加载页面。",
    },
    accessibility: {
      selectLanguage: "选择语言",
    },
    card: {
      selectOption: "请选择",
    },
  },
  ja: {
    site: {
      name: "Templestay",
      title: "Templestay",
      testTitle: "Templestay — テスト",
      surveyTitle: "Templestay — アンケート",
    },
    nav: {
      exitTitle: "終了しますか？",
      exitDesc: "進行中の内容は保存されません。",
      exitConfirm: "終了",
      exitCancel: "続ける",
      bgmLabel: "BGM",
    },
    personalInfo: {
      select: "選択しない",
      notSay: "選択しない",
      male: "男性",
      female: "女性",
      other: "その他",
      nationalityPlaceholder: "国籍を選択",
    },
    common: {
      loading: "読み込み中...",
      error: "エラーが発生しました",
      retry: "再試行",
      close: "閉じる",
      save: "保存",
      cancel: "キャンセル",
      confirm: "確認",
      delete: "削除",
      edit: "編集",
      create: "作成",
      back: "戻る",
      next: "次へ",
      prev: "前へ",
      submit: "送信",
      comingSoon: "近日公開",
      comingSoonDesc: "この機能は本開発で提供される予定です。",
      link: "リンク",
      preparing: "準備中",
    },
    test: {
      errorLoad: "テストを読み込めませんでした。",
      notFound: "テストが見つかりません。",
      resultNotFound: "結果が見つかりません。",
      backHome: "ホームへ",
      shareResult: "結果をシェア",
      shareKakao: "KakaoTalkでシェア",
    },
    survey: {
      start: "開始",
      progress: "{current}/{total}",
      result: "結果を見る",
      share: "シェア",
      errorLoad: "アンケートを読み込めませんでした。",
      notFound: "アンケートが見つかりません。",
      doneTitle: "ご回答ありがとうございました",
      doneDesc: "貴重なご意見は奉恩寺の案内改善に活用されます。",
      backHome: "ホームへ戻る",
      personalInfo: {
        title: "個人情報入力",
        name: "名前",
        age: "年齢",
        gender: "性別",
        nationality: "国籍",
        agreeTerms: "利用規約に同意します",
        agreePrivacy: "個人情報収集に同意します",
      },
    },
    page: {
      errorLoad: "ページを読み込めませんでした。",
    },
    accessibility: {
      selectLanguage: "言語を選択",
    },
    card: {
      selectOption: "選択してください",
    },
  },
};

export function t(lang: Language, path: string): string {
  const keys = path.split(".");
  let current: unknown = uiStrings[lang];
  for (const key of keys) {
    if (typeof current !== "object" || current === null) return path;
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === "string" ? current : path;
}
